import React from 'react';
import {
  IconCalendar,
  IconCircleDot,
  IconClock,
  IconFile,
  IconFolder,
  IconFolders,
  IconList,
  IconNumber123,
  IconTextSize,
} from '@tabler/icons-react';
import type { StudyForm } from '@/types/studyForms';

/**
 * Centralized utility functions for form display and icons
 *
 * Icon logic matches FormIcon.tsx:
 * - Main Form with children: IconFolders (multiple folders)
 * - Main Form without children: IconFolder (single folder)
 * - Subform: IconFile (file icon)
 */

// Form type constants
export const FORM_TYPE_MAIN = 1;
export const FORM_TYPE_SUBFORM = 2;

/**
 * Get form icon component based on form type
 * @param formType - Form type (1 = Main Form, 2 = Subform)
 * @param hasChildren - Whether form has subforms (optional, for more accurate icon)
 * @returns Icon component
 */
export const getFormIcon = (
  formType: number,
  hasChildren?: boolean
): React.ComponentType<{
  size?: number;
  style?: React.CSSProperties;
  color?: string;
  className?: string;
}> => {
  if (formType === FORM_TYPE_MAIN) {
    // Main form with children shows IconFolders (multiple folders)
    // Main form without children shows IconFolder (single folder)
    return hasChildren ? IconFolders : IconFolder;
  } else {
    // Subform always shows file icon
    return IconFile;
  }
};

/**
 * Get form icon color based on form type
 * @param formType - Form type (1 = Main Form, 2 = Subform)
 * @returns CSS color value
 */
export const getFormIconColor = (formType: number): string => {
  return formType === FORM_TYPE_MAIN ? 'var(--blue-9)' : 'var(--orange-9)';
};

/**
 * Get form type label
 * @param formType - Form type (1 = Main Form, 2 = Subform)
 * @returns Form type label string
 */
export const getFormTypeLabel = (formType: number): string => {
  return formType === FORM_TYPE_MAIN ? 'Main Form' : 'Subform';
};

/**
 * Get display name from form (json_schema.displayName or name)
 * @param form - StudyForm object
 * @returns Display name string
 */
export const getFormDisplayName = (form: StudyForm): string => {
  return form.json_schema?.displayName || form.name;
};

/**
 * Check if form is a main form
 * @param formType - Form type (1 = Main Form, 2 = Subform)
 * @returns True if main form
 */
export const isMainForm = (formType: number): boolean => {
  return formType === FORM_TYPE_MAIN;
};

/**
 * Check if form is a subform
 * @param formType - Form type (1 = Main Form, 2 = Subform)
 * @returns True if subform
 */
export const isSubform = (formType: number): boolean => {
  return formType === FORM_TYPE_SUBFORM;
};

/**
 * Get field icon component based on field type
 * @param fieldType - Field type ('text' | 'number' | 'date' | 'time' | 'select' | 'radio')
 * @returns Icon component
 */
export const getFieldIcon = (
  fieldType: string
): React.ComponentType<{
  size?: number;
  style?: React.CSSProperties;
  color?: string;
  className?: string;
}> => {
  switch (fieldType) {
    case 'text':
      return IconTextSize;
    case 'number':
      return IconNumber123;
    case 'date':
      return IconCalendar;
    case 'time':
      return IconClock;
    case 'select':
      return IconList;
    case 'radio':
      return IconCircleDot;
    default:
      return IconFile;
  }
};

/**
 * Get field type label
 * @param fieldType - Field type ('text' | 'number' | 'date' | 'time' | 'select' | 'radio')
 * @returns Field type label string
 */
export const getFieldTypeLabel = (fieldType: string): string => {
  const labels: Record<string, string> = {
    text: 'Text',
    number: 'Number',
    date: 'Date',
    time: 'Time',
    select: 'Select',
    radio: 'Radio',
  };
  return labels[fieldType] || fieldType;
};
